import React, { useEffect, useState } from "react";
import { Button } from "../../../../ui/components/Button";
import { Select } from "../../../../ui/components/Select";
import { TextField } from "../../../../ui/components/TextField";
import {
  createTcsInZoho,
  fetchTcsLedgers,
  fetchTcsSections,
  updateTcsInZoho,
  TcsSection,
  TdsLedgerSlot,
} from "../../../../services/api";
import type { TcsDTO } from "../../../../shared/data/models/TaxDTOs";
import { LedgerSlotSelect } from "./LedgerSlotSelect";
import { financialYearStart, TAX_NAME_MAX } from "./tdsTable";

interface TcsRateDraft {
  tax_name: string;
  tax_percentage: string;
  tax_specific_type: string;
  start_date: string;
  end_date: string;
  /** NOTE: on a TCS rate tax_account_id is the PAYABLE. Zoho's naming. */
  tax_account_id: string;
  tcs_receivable_account_id: string;
}

const emptyDraft = (): TcsRateDraft => ({
  tax_name: "",
  tax_percentage: "",
  tax_specific_type: "",
  start_date: financialYearStart(new Date()),
  end_date: "",
  tax_account_id: "",
  tcs_receivable_account_id: "",
});

/**
 * The row as this form holds it.
 *
 * The dates stay blank and the accounts stay unset because `tcs` has neither —
 * see the note on the edit path below for why they are then left off the form
 * entirely rather than shown empty.
 */
const draftFrom = (rate: TcsDTO): TcsRateDraft => ({
  tax_name: rate.tax_name || "",
  tax_percentage: String(rate.tax_rate ?? ""),
  tax_specific_type: rate.tax_specific_type || "",
  start_date: "",
  end_date: "",
  tax_account_id: "",
  tcs_receivable_account_id: "",
});

interface TcsRateFormProps {
  /** The rate being changed, or null to add one. */
  rate: TcsDTO | null;
  /** notice for the panel, plus the Zoho tax id of a rate just written. */
  onSaved: (notice: string, writtenTaxId: string | null) => void;
  onCancel: () => void;
}

/**
 * Add or change a TCS rate, INSIDE the panel rather than over it.
 *
 * The inline twin of TcsRateDialog, for the same reason its TDS counterpart is
 * inline: the register stays on screen while the form is filled. The dialog is
 * left in place untouched.
 *
 * ONE FORM FOR ADD AND EDIT, because Zoho's edit dialog IS its add dialog with
 * the fields filled in. What differs is which call it makes, and that an edit
 * offers neither the dates nor the accounts:
 *
 * `tcs` stores no start_date, no end_date and no account ids, so there is nothing
 * to prefill them from. Zoho's update is a FULL REPLACE, which makes a blank
 * field on this form a live instruction rather than an omission — a date picker
 * starting empty would move when the rate applies, and an account select reading
 * "Zoho picks" would claim the rate names nothing. Both are left out and the
 * server carries Zoho's own values forward instead.
 *
 * The nature-of-collection list is read live from the org rather than from a
 * seeded table: TDS has a catalogue of its own, TCS has none, and a list Zoho
 * owns is better read from Zoho than copied and left to drift.
 */
export const TcsRateForm: React.FC<TcsRateFormProps> = ({
  rate,
  onSaved,
  onCancel,
}) => {
  const isCreate = rate === null;
  const [draft, setDraft] = useState<TcsRateDraft>(() =>
    rate ? draftFrom(rate) : emptyDraft(),
  );
  const [sections, setSections] = useState<TcsSection[]>([]);
  const [ledgers, setLedgers] = useState<{
    payable: TdsLedgerSlot;
    receivable: TdsLedgerSlot;
  } | null>(null);
  const [showLedgers, setShowLedgers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    fetchTcsSections()
      .then((r) => {
        if (live) setSections(r.data.sections);
      })
      .catch((err) => {
        if (live) {
          setError(
            err instanceof Error
              ? err.message
              : "Could not read the nature-of-collection list",
          );
        }
      });

    // Only the add form offers the accounts, so only it pays for them.
    if (isCreate) {
      fetchTcsLedgers()
        .then((r) => {
          if (live) setLedgers(r.data.slots);
        })
        .catch(() => {
          // Not surfaced: leaving both blank lets Zoho pick, which is the
          // default path anyway. The override link simply offers nothing.
        });
    }

    return () => {
      live = false;
    };
  }, [isCreate]);

  const patch = (next: Partial<TcsRateDraft>) =>
    setDraft((current) => ({ ...current, ...next }));

  // Edit opens from a row that can be the whole table above where this renders,
  // so the form comes to the operator. Add does not scroll: its button is where
  // the form appears. The TDS form's note has the reasoning.
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!isCreate) {
      rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [isCreate]);

  const pickSection = (value: string) => {
    const picked = sections.find((s) => s.tax_specific_type === value);
    const previous = sections.find(
      (s) => s.tax_specific_type === draft.tax_specific_type,
    );
    // Zoho seeds the name from the section and lets it be overwritten. Measured
    // against what the previous section would have put there, not against blank,
    // so a second pick does not keep the first one's name.
    const untouched =
      draft.tax_name.trim() === "" || draft.tax_name === previous?.description;
    patch({
      tax_specific_type: value,
      tax_name: untouched ? picked?.description ?? "" : draft.tax_name,
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      if (rate === null) {
        const result = await createTcsInZoho(draft);
        onSaved(
          result.data.local_warning ||
            "TCS rate created in Zoho Books and saved.",
          result.data.tcs_id || null,
        );
      } else {
        // Only the three fields this form shows travel. The dates and the
        // accounts are deliberately ABSENT KEYS rather than blanks — the server
        // reads Zoho's own values for those, because a full replace would
        // otherwise take a blank at its word.
        const result = await updateTcsInZoho(rate.tcs_id, {
          tax_name: draft.tax_name,
          tax_percentage: draft.tax_percentage,
          tax_specific_type: draft.tax_specific_type,
        });
        onSaved(
          result.data.local_warning || result.message || "TCS rate updated.",
          result.data.tcs_id || rate.tcs_id,
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
      setSaving(false);
    }
  };

  const ledgerName = (slot: TdsLedgerSlot | undefined, fallback: string) => {
    const id = slot?.default_account_id;
    if (!id) return fallback;
    return (
      slot?.accounts.find((a) => a.account_id === id)?.account_name ?? fallback
    );
  };

  const canSave =
    draft.tax_name.trim() !== "" &&
    draft.tax_percentage.trim() !== "" &&
    draft.tax_specific_type !== "";

  return (
    <div
      ref={rootRef}
      className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 p-4"
    >
      <div className="flex flex-col items-start gap-1">
        <span className="text-body-bold font-body-bold text-default-font">
          {isCreate ? "New TCS rate" : rate.tax_name}
        </span>
        <span className="text-caption font-caption text-subtext-color">
          {isCreate
            ? "Created in Zoho Books, then mirrored here."
            : // Said out loud, because it is the part that surprises: a rate
              // already used on an invoice cannot be changed in place, so Zoho
              // ends it and makes a replacement.
              `${rate.tax_rate}% · saved to Zoho Books. A rate already used on an invoice is replaced rather than changed.`}
        </span>
      </div>

      <Select
        className="w-full"
        label="Nature of collection"
        placeholder="Select a nature of collection"
        helpText=""
        value={draft.tax_specific_type || ""}
        onValueChange={pickSection}
      >
        {sections.map((s) => (
          <Select.Item key={s.tax_specific_type} value={s.tax_specific_type}>
            {s.description} ({s.section_no})
          </Select.Item>
        ))}
      </Select>

      <div className="flex w-full flex-wrap items-start gap-4">
        <TextField
          className="grow"
          label="Tax name"
          helpText={
            draft.tax_name.length > TAX_NAME_MAX - 10
              ? `${draft.tax_name.length}/${TAX_NAME_MAX}`
              : "what appears on the invoice"
          }
        >
          <TextField.Input
            placeholder="e.g. TCS on Scrap"
            maxLength={TAX_NAME_MAX}
            value={draft.tax_name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              patch({ tax_name: e.target.value })
            }
          />
        </TextField>
        <TextField className="w-32" label="Rate (%)" helpText="">
          <TextField.Input
            placeholder="1"
            value={draft.tax_percentage}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              patch({ tax_percentage: e.target.value })
            }
          />
        </TextField>
      </div>

      {isCreate && (
        <div className="flex w-full flex-wrap items-start gap-4">
          <TextField className="grow" label="Applicable from" helpText="">
            <TextField.Input
              type="date"
              value={draft.start_date}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                patch({ start_date: e.target.value })
              }
            />
          </TextField>
          <TextField
            className="grow"
            label="Until"
            helpText="blank = open-ended"
          >
            <TextField.Input
              type="date"
              value={draft.end_date}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                patch({ end_date: e.target.value })
              }
            />
          </TextField>
        </div>
      )}

      {isCreate && (
        <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-default-background p-3">
          {!showLedgers ? (
            <span className="text-caption font-caption text-subtext-color">
              Tracked under{" "}
              <span className="text-caption-bold font-caption-bold text-default-font">
                {ledgerName(ledgers?.payable, "TCS Payable")}
              </span>{" "}
              and{" "}
              <span className="text-caption-bold font-caption-bold text-default-font">
                {ledgerName(ledgers?.receivable, "TCS Receivable")}
              </span>
              .{" "}
              {/* Not a hedge. An organisation that has never had a TCS rate has
                  neither account in its chart, and Zoho makes both on the first
                  one saved. */}
              {ledgers !== null &&
                !ledgers.payable.default_account_id &&
                !ledgers.receivable.default_account_id &&
                "Zoho creates them if they are not there yet. "}
              <button
                type="button"
                className="text-caption-bold font-caption-bold text-brand-700 underline"
                onClick={() => setShowLedgers(true)}
              >
                Use different accounts
              </button>
            </span>
          ) : (
            <>
              <div className="flex w-full items-baseline justify-between gap-2">
                <span className="text-caption-bold font-caption-bold text-default-font">
                  Accounts
                </span>
                <button
                  type="button"
                  className="text-caption font-caption text-brand-700 underline"
                  onClick={() => {
                    setShowLedgers(false);
                    patch({ tax_account_id: "", tcs_receivable_account_id: "" });
                  }}
                >
                  Use Zoho's defaults
                </button>
              </div>
              <div className="flex w-full flex-wrap items-start gap-4">
                {/* tax_account_id is the PAYABLE on a TCS rate — it is the
                    receivable on a TDS one. Zoho's naming, not ours. */}
                <LedgerSlotSelect
                  label="Payable"
                  slot="payable"
                  kind="tcs"
                  accounts={ledgers?.payable.accounts ?? []}
                  value={draft.tax_account_id}
                  onChange={(value) => patch({ tax_account_id: value })}
                  onSlotsRefreshed={setLedgers}
                />
                <LedgerSlotSelect
                  label="Receivable"
                  slot="receivable"
                  kind="tcs"
                  accounts={ledgers?.receivable.accounts ?? []}
                  value={draft.tcs_receivable_account_id}
                  onChange={(value) =>
                    patch({ tcs_receivable_account_id: value })
                  }
                  onSlotsRefreshed={setLedgers}
                />
              </div>
            </>
          )}
        </div>
      )}

      {error && (
        <div className="flex w-full items-start rounded-lg border border-solid border-red-200 bg-red-50 p-3">
          <span className="text-body font-body text-red-600">{error}</span>
        </div>
      )}

      <div className="flex w-full items-center justify-end gap-2">
        <Button
          variant="neutral-tertiary"
          size="small"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          variant="brand-primary"
          size="small"
          onClick={handleSave}
          disabled={saving || !canSave}
        >
          {saving
            ? isCreate
              ? "Creating..."
              : "Saving..."
            : isCreate
              ? "Create in Zoho"
              : "Save to Zoho"}
        </Button>
      </div>
    </div>
  );
};

export default TcsRateForm;
